import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import GameDataTooltip from './GameDataTooltip';
import CommonBubble from '@home/components/CommonBubble';
import './index.scss';

/**
 * color - 数据项的颜色标识
 * value - 数据项的具体数值
 * img - 数据项关联的图片资源路径
 */
export type TGameData = {
  color: string;
  value: string;
  img: string;
  name: string;
};

/**
 * name - 组件的名称
 * className - 组件的CSS类名
 * style - 组件的内联样式对象
 * dataList - 游戏数据项的数组，用于在组件中进行比较展示
 * maxlength - 一页最大展示的卡片数量
 */
interface IGameDataComparisonProps {
  className?: string;
  style?: React.CSSProperties;
  dataList: { name: string; list: TGameData[]; valueFormat?: string; }[];
  maxlength?: number;
  tooltipTitle: string;
}

export default (props: IGameDataComparisonProps) => {
  const { dataList, className, style, maxlength = 6, tooltipTitle } = props;

  const ref = useRef<HTMLDivElement>(null);

  const [clientWidth, setIClientWidth] = useState<number>(0);

  const [xScrollWidth, setXScrollWidth] = useState(0);

  useEffect(() => {
    const scrollableDiv = ref.current;

    const updateWidth = () => {
      if (scrollableDiv) {
        // 去除padding和margin
        setIClientWidth(scrollableDiv.getBoundingClientRect().width - 40 - 240);
      }
    };
    window.addEventListener('resize', updateWidth);
    updateWidth();


    const handleScroll = () => {
      if (scrollableDiv) {
        setXScrollWidth(scrollableDiv.scrollLeft);
      }
    };

    if (scrollableDiv) {
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div
      className={classNames(
        'dbh-studio-game-data-comparison-container',
        className,
        { 'dbh-studio-game-data-comparison-container-scroll': xScrollWidth > 0 },
      )}
      style={style}
      ref={ref}
    >
      <div className="dbh-studio-game-data-list-container">
        {dataList?.map((item, index) => {
          const { name, list, valueFormat = '' } = item || {};
          // 获取最大值
          const maxValue = Math.max(
            ...list?.map((item) => Number(item.value || 0)),
          );
          return (
            <CommonBubble
              key={index}
              placement="right"
              overlayClassName="dbh-studio-game-data-tooltip"
              content={
                <GameDataTooltip
                  valueHeader={tooltipTitle}
                  nameHeader={item.name}
                  data={item.list}
                  dataFormatRule={valueFormat}
                />
              }
            >
              <div
                className={classNames('dbh-studio-game-data-item-container', {
                  'dbh-studio-game-data-item-fixed':
                    dataList.length > maxlength && index === 0,
                  'dbh-studio-game-data-item-only': dataList.length === 1,
                  'dbh-studio-game-data-item-more': dataList.length > maxlength,
                })}
                style={{
                  width:
                    dataList.length > maxlength ? clientWidth / maxlength : '',
                }}
              >
                <div>{name}</div>
                {list?.map((item1) => {
                  const { color, value } = item1;
                  return (
                    <div className="dbh-studio-game-data" key={value}>
                      {maxValue > 0 && (
                        <div className='dbh-studio-game-data-progress-container'>
                          <div
                            className="dbh-studio-game-data-progress"
                            style={{
                              background: color,
                              width: `${(Number(value) / maxValue) * 100}%`,
                            }}
                          ></div>
                        </div>
                      )}
                      <div className="dbh-studio-game-data-value">
                        {dataFormat({
                          value,
                          formatRule: valueFormat,
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CommonBubble>
          );
        })}
      </div>
    </div>
  );
};
